import { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CheckCircle, Globe, Edit, Sparkles } from "lucide-react";
import { toast } from "sonner";
import type { WebsiteSuggestion } from "@/components/website-suggest/types";
import {
  AddressAutocomplete,
  type AddressAutocompleteStatus,
} from "../website-form/AddressAutocomplete";
import { CategoryCombobox } from "../website-suggest/CategoryCombobox";
import { RegionSelector } from "../website-suggest/RegionSelector";
import {
  checkSuggestionUrlBoth,
  guessRegionByZipCode,
  suggestCategoryByAi,
} from "@/services/websiteSuggestion.service";
import { buildCategoryBreadcrumbLabel } from "@/utils/categoryLabel";
import {
  getAddressAutocompleteEnabled,
  getAiCategorySuggestionEnabled,
} from "@/services/publicSystemSettings.service";
import {
  logGeocodingEvent,
  type GeocodingConfidence,
  type GeocodingRegion,
  type GeocodingRegionType,
  type GeocodingSuggestion,
} from "@/services/geocoding.service";

const requiredAsteriskStyle = { color: "var(--primary)" } as const;
const renderRequiredLabel = (label: string) => {
  const starIndex = label.indexOf("*");
  if (starIndex === -1) return label;
  const before = label.slice(0, starIndex);
  const after = label.slice(starIndex + 1);
  return (
    <>
      {before}
      <span style={requiredAsteriskStyle}>*</span>
      {after}
    </>
  );
};

// Backend-aligned max length constants
const MAX_TITLE_LEN = 200;
const MAX_DESCRIPTION_LEN = 500;
const MAX_URL_LEN = 512;
const MAX_ADDRESS_LEN = 512;
const MAX_ZIP_LEN = 5;

// Normalize URL like backend:
// - trim
// - ensure scheme (https default)
// - lowercase host + remove leading "www."
// - keep path + query
// - drop fragment (#...)
const normalizeUrlKeepPathQuery = (input: string) => {
  const raw = input.trim();
  if (!raw) return raw;

  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

  let u: URL;
  try {
    u = new URL(withScheme);
  } catch {
    return withScheme.split("#")[0].trim();
  }

  let host = (u.hostname || "").toLowerCase();
  if (host.startsWith("www.")) host = host.slice(4);

  const path = u.pathname || "";
  const query = u.search || "";
  return `https://${host}${path}${query}`;
};

type UrlErrorKey =
  | "websiteSuggest.errorUrlExists"
  | "websiteSuggest.errorUrlVerifyFailed";

interface SuggestionsPanelProps {
  suggestions: WebsiteSuggestion[];
  onApproveSuggestion: (
    id: string,
    modified?: Partial<WebsiteSuggestion>
  ) => void;
  onRejectSuggestion: (id: string) => void;
  onSaveSuggestion: (
    id: string,
    modified: Partial<WebsiteSuggestion>
  ) => Promise<void>;
}

type SuggestionEditFormData = {
  id: string;
  title: string;
  url: string;
  description: string;
  address: string;
  zipCode: string;
  category1Id: string | null;
  category1Name: string;
  categoryPathIds: string[];
  categoryPathNames: string[];
  category1Depth: number | null;
  regionId: string | null;
  regionName: string;
  regionPathIds: string[];
  regionPathNames: string[];
};

const toEditFormData = (suggestion: WebsiteSuggestion): SuggestionEditFormData => ({
  id: suggestion.id,
  title: suggestion.title,
  url: suggestion.url,
  description: suggestion.description,
  address: suggestion.address ?? "",
  zipCode: suggestion.zipCode ?? "",
  category1Id: suggestion.category1Id ?? null,
  category1Name: suggestion.category1 ?? "",
  categoryPathIds: suggestion.categoryPathIds ?? [],
  categoryPathNames: suggestion.categoryPathNames ?? [],
  category1Depth: null,
  regionId: suggestion.regionId ?? null,
  regionName: suggestion.region ?? "",
  regionPathIds: suggestion.regionPathIds ?? [],
  regionPathNames: suggestion.regionPathNames ?? [],
});

export function ManageSuggestionsPanel({
  suggestions,
  onApproveSuggestion,
  onRejectSuggestion,
  onSaveSuggestion,
}: SuggestionsPanelProps) {
  const { t, language } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const mutedIconStyle = {
    color: "color-mix(in srgb, var(--foreground) 40%, var(--surface))",
  } as const;
  const suggestionCardStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 10%, var(--background))",
    borderColor:
      "color-mix(in srgb, var(--accent) 22%, var(--background))",
  } as const;
  const overlayStyle = {
    backgroundColor: "color-mix(in srgb, var(--foreground) 50%, transparent)",
  } as const;
  const errorTextStyle = {
    color: "color-mix(in srgb, var(--accent) 82%, var(--foreground))",
  } as const;
  const [editingSuggestion, setEditingSuggestion] =
    useState<SuggestionEditFormData | null>(null);
  const [openCategory1, setOpenCategory1] = useState(false);
  const [openRegion, setOpenRegion] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const pendingSuggestions = suggestions.filter((s) => s.status === "pending");

  const [autoConfidence, setAutoConfidence] =
    useState<GeocodingConfidence | null>(null);
  const [autoStatus, setAutoStatus] =
    useState<AddressAutocompleteStatus>("idle");
  const [autoRegionType, setAutoRegionType] =
    useState<GeocodingRegionType | null>(null);
  const [addressAutocompleteEnabled, setAddressAutocompleteEnabled] =
    useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFeatureEnabled, setAiFeatureEnabled] = useState(true);

  const [fieldErrors, setFieldErrors] = useState({
    title: false,
    url: false,
    description: false,
  });
  const [showValidation, setShowValidation] = useState(false);
  const [showCategorizationValidation, setShowCategorizationValidation] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [urlErrorKey, setUrlErrorKey] = useState<UrlErrorKey | null>(null);

  const [wasDescriptionTooLong, setWasDescriptionTooLong] = useState(false);
  const [wasTitleTooLong, setWasTitleTooLong] = useState(false);
  const [wasUrlTooLong, setWasUrlTooLong] = useState(false);
  const [wasAddressTooLong, setWasAddressTooLong] = useState(false);
  const [wasZipTooLong, setWasZipTooLong] = useState(false);

  const [autoRegionLoading, setAutoRegionLoading] = useState(false);
  const [autoRegionApplied, setAutoRegionApplied] = useState(false);
  const setEditingFormData = (
    next:
      | SuggestionEditFormData
      | ((prev: SuggestionEditFormData) => SuggestionEditFormData)
  ) => {
    setEditingSuggestion((prev) => {
      if (!prev) return prev;
      return typeof next === "function" ? next(prev) : next;
    });
  };

  useEffect(() => {
    if (!editingSuggestion) return;
    setAiLoading(false);
    setShowValidation(false);
    setShowCategorizationValidation(false);
    setFieldErrors({ title: false, url: false, description: false });
    setUrlErrorKey(null);
    setWasDescriptionTooLong(false);
    setWasTitleTooLong(false);
    setWasUrlTooLong(false);
    setWasAddressTooLong(false);
    setWasZipTooLong(false);
    setAutoRegionApplied(false);
    setAutoConfidence(null);
    setAutoStatus("idle");
    setAutoRegionType(null);
  }, [editingSuggestion?.id]);

  useEffect(() => {
    let isMounted = true;
    const loadSetting = async () => {
      const enabled = await getAddressAutocompleteEnabled();
      if (isMounted) setAddressAutocompleteEnabled(enabled);
    };
    loadSetting();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadAiSetting = async () => {
      const enabled = await getAiCategorySuggestionEnabled();
      if (isMounted) setAiFeatureEnabled(enabled);
    };
    loadAiSetting();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (aiFeatureEnabled) return;
    setAiLoading(false);
  }, [aiFeatureEnabled]);


  useEffect(() => {
    if (!editingSuggestion) return;

    setEditingFormData((prev) => ({
      ...prev,
      category1Id: null,
      category1Name: "",
      category1Depth: null,
      categoryPathIds: [],
      categoryPathNames: [],
    }));

    setAiLoading(false);
    setOpenCategory1(false);
  }, [language]);

  const clearRegionSelection = useCallback(() => {
    setEditingFormData((prev) => ({
      ...prev,
      regionId: null,
      regionName: "",
      regionPathIds: [],
      regionPathNames: [],
    }));
  }, [setEditingFormData]);

  useEffect(() => {
    if (!editingSuggestion) return;

    const z = (editingSuggestion.zipCode ?? "").trim();
    const hasRegionSelection =
      Boolean(editingSuggestion.regionId) ||
      Boolean(editingSuggestion.regionName) ||
      (editingSuggestion.regionPathIds?.length ?? 0) > 0 ||
      (editingSuggestion.regionPathNames?.length ?? 0) > 0;

    if (hasRegionSelection && !autoRegionApplied) {
      return;
    }

    if (!z) {
      setAutoRegionLoading(false);
      setAutoRegionApplied(false);
      return;
    }

    if (!/^\d{5}$/.test(z)) {
      setAutoRegionLoading(false);
      if (autoRegionApplied) {
        clearRegionSelection();
        setAutoRegionApplied(false);
      }
      return;
    }

    const timer = window.setTimeout(async () => {
      setAutoRegionLoading(true);

      const res = await guessRegionByZipCode(z);

      setAutoRegionLoading(false);

      if (!res.ok) return;

      if (!res.regionId) {
        if (autoRegionApplied) {
          clearRegionSelection();
          setAutoRegionApplied(false);
        }
        return;
      }

      setEditingFormData((prev) => {
        const userHasManualRegion = Boolean(prev.regionId) && !autoRegionApplied;
        if (userHasManualRegion) return prev;

        const name =
          res.regionName && res.regionName.trim() ? res.regionName.trim() : "";

        return {
          ...prev,
          regionId: res.regionId,
          regionName: name,
          regionPathIds: [res.regionId],
          regionPathNames: name ? [name] : [],
        };
      });

      setAutoRegionApplied(true);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [
    editingSuggestion?.zipCode,
    editingSuggestion?.regionId,
    editingSuggestion?.regionName,
    editingSuggestion?.regionPathIds,
    editingSuggestion?.regionPathNames,
    autoRegionApplied,
    clearRegionSelection,
    setEditingFormData,
  ]);

  const handleAutoSelect = useCallback(
    (suggestion: GeocodingSuggestion) => {
      const resolveRegionType = (region: GeocodingRegion | null) => {
        if (!region) return null;
        if (region.type) return region.type;
        const depth = Math.max(region.pathIds.length, region.pathNames.length);
        if (depth >= 4) return "POSTAL_CODE";
        if (depth === 3) return "DISTRICT";
        if (depth === 2) return "ADMINISTRATIVE_DISTRICT";
        if (depth === 1 || region.id) return "STATE";
        return null;
      };

      setAutoStatus("idle");
      setAutoConfidence(suggestion.confidence);
      setAutoRegionApplied(false);
      setAutoRegionType(resolveRegionType(suggestion.region));
      void logGeocodingEvent({
        event: "resolution_selected",
        confidence: suggestion.confidence,
      });

      setEditingFormData((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          address: suggestion.label,
          zipCode: suggestion.postalCode ?? prev.zipCode,
        };
        if (suggestion.region) {
          const pathIds =
            suggestion.region.pathIds.length > 0
              ? suggestion.region.pathIds
              : [suggestion.region.id];
          const pathNames =
            suggestion.region.pathNames.length > 0
              ? suggestion.region.pathNames
              : [suggestion.region.name];

          next.regionId = suggestion.region.id;
          next.regionName = suggestion.region.name;
          next.regionPathIds = pathIds;
          next.regionPathNames = pathNames;
        }
        return next;
      });

      if (!suggestion.region) {
        setAutoStatus("invalid_response");
      }
    },
    [setEditingFormData]
  );

  const lastLoggedStatusRef = useRef<AddressAutocompleteStatus>("idle");
  useEffect(() => {
    if (!addressAutocompleteEnabled) return;
    if (autoStatus === "idle" || autoStatus === "no_results") {
      lastLoggedStatusRef.current = autoStatus;
      return;
    }
    if (lastLoggedStatusRef.current === autoStatus) return;

    lastLoggedStatusRef.current = autoStatus;
    void logGeocodingEvent({
      event: "autocomplete_status",
      reason: autoStatus,
    });
  }, [addressAutocompleteEnabled, autoStatus]);

  const totalPages = Math.max(1, Math.ceil(pendingSuggestions.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedSuggestions = pendingSuggestions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const titleTooLong = editingSuggestion
    ? editingSuggestion.title.length > MAX_TITLE_LEN
    : false;
  const urlTooLong = editingSuggestion
    ? editingSuggestion.url.length > MAX_URL_LEN
    : false;
  const descriptionTooLong = editingSuggestion
    ? editingSuggestion.description.length > MAX_DESCRIPTION_LEN
    : false;
  const addressTooLong = editingSuggestion
    ? (editingSuggestion.address ?? "").length > MAX_ADDRESS_LEN
    : false;
  const zipTooLong = editingSuggestion
    ? (editingSuggestion.zipCode ?? "").length > MAX_ZIP_LEN
    : false;

  const hasValidZip = editingSuggestion
    ? /^\d{5}$/.test((editingSuggestion.zipCode ?? "").trim())
    : false;
  const categoryMissing =
    showCategorizationValidation && editingSuggestion
      ? !editingSuggestion.category1Id
      : false;
  const regionMissing =
    showCategorizationValidation && editingSuggestion
      ? !editingSuggestion.regionId && !hasValidZip
      : false;

  const requiredFieldsFilled = editingSuggestion
    ? Boolean(editingSuggestion.title.trim()) &&
      Boolean(editingSuggestion.url.trim()) &&
      Boolean(editingSuggestion.description.trim()) &&
      Boolean(editingSuggestion.category1Id) &&
      (Boolean(editingSuggestion.regionId) || hasValidZip)
    : false;
  const aiMissingRequiredFields = editingSuggestion
    ? !editingSuggestion.title.trim() ||
      !editingSuggestion.url.trim() ||
      !editingSuggestion.description.trim()
    : true;
  const approveWithChangesDisabled = isSubmitting || !requiredFieldsFilled;
  const showApproveTooltip = !requiredFieldsFilled;

  const titleInvalid = (showValidation && fieldErrors.title) || titleTooLong;
  const urlInvalid =
    (showValidation && fieldErrors.url) || urlErrorKey !== null || urlTooLong;
  const descriptionInvalid =
    (showValidation && fieldErrors.description) || descriptionTooLong;
  const zipInvalid = zipTooLong;

  const confidenceLabel =
    autoConfidence === "EXACT"
      ? t("websiteSuggest.addressConfidenceExact")
      : autoConfidence === "AMBIGUOUS"
        ? t("websiteSuggest.addressConfidenceAmbiguous")
        : autoConfidence === "PARTIAL"
          ? autoRegionType === "STATE"
            ? t("websiteSuggest.addressConfidenceState")
            : autoRegionType === "ADMINISTRATIVE_DISTRICT"
              ? t("websiteSuggest.addressConfidenceAdminDistrict")
              : autoRegionType === "DISTRICT"
                ? t("websiteSuggest.addressConfidenceDistrict")
                : autoRegionType === "POSTAL_CODE"
                  ? t("websiteSuggest.addressConfidenceExact")
                  : t("websiteSuggest.addressConfidencePartial")
          : null;

  const handleAiSuggest = async () => {
    if (!editingSuggestion) return;

    const titleEmpty = !editingSuggestion.title.trim();
    const urlEmpty = !editingSuggestion.url.trim();
    const descriptionEmpty = !editingSuggestion.description.trim();

    if (titleEmpty || urlEmpty || descriptionEmpty) {
      toast.error(t("websiteSuggest.aiNeedTitleAndDescription"));
      return;
    }

    if (editingSuggestion.title.length > MAX_TITLE_LEN) {
      toast.error(
        t("websiteSuggest.errorTitleTooLong").replace("{max}", String(MAX_TITLE_LEN))
      );
      return;
    }
    if (editingSuggestion.description.length > MAX_DESCRIPTION_LEN) {
      toast.error(
        t("websiteSuggest.errorDescriptionTooLong").replace(
          "{max}",
          String(MAX_DESCRIPTION_LEN)
        )
      );
      return;
    }

    if (aiLoading) return;

    setAiLoading(true);
    try {
      const res = await suggestCategoryByAi({
        title: editingSuggestion.title.trim(),
        description: editingSuggestion.description.trim(),
        lang: language === "de" ? "de" : "en",
      });

      if (!res.ok) {
        switch (res.kind) {
          case "timeout":
            toast.error(t("websiteSuggest.aiTimeout"));
            break;
          case "bad_gateway":
            toast.error(t("websiteSuggest.aiBadGateway"));
            break;
          default:
            toast.error(t("websiteSuggest.aiFailed"));
            break;
        }
        return;
      }

      const data = res.data;

      const pathIds = Array.isArray(data.categoryPathIds) ? data.categoryPathIds : [];
      const pathNames = Array.isArray(data.categoryPathNames) ? data.categoryPathNames : [];

      const leafId = pathIds.length > 0 ? pathIds[pathIds.length - 1] : data.categoryId;
      const leafName = pathNames.length > 0 ? pathNames[pathNames.length - 1] : data.categoryName;

      setEditingFormData((prev) => ({
        ...prev,
        category1Id: leafId,
        category1Name: leafName,
        category1Depth: typeof data.depth === "number" ? data.depth : null,
        categoryPathIds: pathIds,
        categoryPathNames: pathNames,
      }));

      setOpenCategory1(false);

      toast.success(t("websiteSuggest.aiApplied"));
    } finally {
      setAiLoading(false);
    }
  };

  const handleApproveSuggestion = (
    suggestion: WebsiteSuggestion,
    modified?: Partial<WebsiteSuggestion>
  ) => {
    if (modified && editingSuggestion) {
      onApproveSuggestion(suggestion.id, modified);
      toast.success(t("websiteManagement.suggestionApprovedWithChanges"));
      setEditingSuggestion(null);
    } else {
      onApproveSuggestion(suggestion.id);
      toast.success(t("websiteManagement.suggestionApproved"));
    }
  };

  const buildModifiedSuggestion = async (
    requireCategorization: boolean
  ): Promise<{
    original: WebsiteSuggestion;
    modified: Partial<WebsiteSuggestion>;
  } | null> => {
    if (!editingSuggestion) return null;

    setShowValidation(true);
    setShowCategorizationValidation(requireCategorization);
    setUrlErrorKey(null);

    const titleEmpty = !editingSuggestion.title.trim();
    const urlEmpty = !editingSuggestion.url.trim();
    const descriptionEmpty = !editingSuggestion.description.trim();

    setFieldErrors({
      title: titleEmpty,
      url: urlEmpty,
      description: descriptionEmpty,
    });

    if (titleEmpty || urlEmpty || descriptionEmpty) {
      toast.error(t("websiteSuggest.fillAllFields"));
      const firstErrorId = titleEmpty
        ? "suggestion-title"
        : urlEmpty
        ? "suggestion-url"
        : "suggestion-description";
      const el = document.getElementById(firstErrorId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return null;
    }

    if (editingSuggestion.title.length > MAX_TITLE_LEN) {
      toast.error(t("websiteSuggest.errorTitleTooLong").replace("{max}", String(MAX_TITLE_LEN)));
      document
        .getElementById("suggestion-title")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return null;
    }

    if (editingSuggestion.url.length > MAX_URL_LEN) {
      toast.error(t("websiteSuggest.errorUrlTooLong").replace("{max}", String(MAX_URL_LEN)));
      document
        .getElementById("suggestion-url")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return null;
    }

    if (editingSuggestion.description.length > MAX_DESCRIPTION_LEN) {
      toast.error(
        t("websiteSuggest.errorDescriptionTooLong").replace("{max}", String(MAX_DESCRIPTION_LEN))
      );
      document
        .getElementById("suggestion-description")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return null;
    }

    if ((editingSuggestion.address ?? "").length > MAX_ADDRESS_LEN) {
      toast.error(t("websiteSuggest.errorAddressTooLong").replace("{max}", String(MAX_ADDRESS_LEN)));
      document
        .getElementById("suggestion-address")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return null;
    }

    if ((editingSuggestion.zipCode ?? "").length > MAX_ZIP_LEN) {
      toast.error(t("websiteSuggest.errorZipTooLong").replace("{max}", String(MAX_ZIP_LEN)));
      document.getElementById("zipCode")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return null;
    }

    if (requireCategorization) {
      const missingCategory = !editingSuggestion.category1Id;
      const missingRegion = !editingSuggestion.regionId && !hasValidZip;

      if (missingCategory || missingRegion) {
        if (missingCategory) {
          toast.error(t("websiteManagement.categoryRequiredError"));
        }
        if (missingRegion) {
          toast.error(t("websiteManagement.regionRequiredError"));
        }

        const targetId = missingCategory
          ? "suggestion-category"
          : "suggestion-region";
        document.getElementById(targetId)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return null;
      }
    }

    const normalizedUrl = normalizeUrlKeepPathQuery(editingSuggestion.url);

    const original = suggestions.find((s) => s.id === editingSuggestion.id);
    if (!original) return null;

    const originalUrl = normalizeUrlKeepPathQuery(original.url ?? "");
    if (normalizedUrl && normalizedUrl !== originalUrl) {
      const check = await checkSuggestionUrlBoth(normalizedUrl);

      if (!check.ok) {
        setFieldErrors((prev) => ({ ...prev, url: true }));
        setUrlErrorKey("websiteSuggest.errorUrlVerifyFailed");
        toast.error(t("websiteSuggest.errorUrlVerifyFailed"));
        return null;
      }

      if (check.exists) {
        setFieldErrors((prev) => ({ ...prev, url: true }));
        setUrlErrorKey("websiteSuggest.errorUrlExists");
        toast.error(t("websiteSuggest.errorUrlExists"));
        document
          .getElementById("suggestion-url")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return null;
      }
    }

    const modified: Partial<WebsiteSuggestion> = {
      title: editingSuggestion.title.trim(),
      url: normalizedUrl,
      description: editingSuggestion.description.trim(),
      address: editingSuggestion.address,
      zipCode: editingSuggestion.zipCode,
      category1Id: editingSuggestion.category1Id ?? undefined,
      category1: editingSuggestion.category1Name,
      categoryPathIds: editingSuggestion.categoryPathIds,
      categoryPathNames: editingSuggestion.categoryPathNames,
      regionId: editingSuggestion.regionId ?? undefined,
      region: editingSuggestion.regionName,
      regionPathIds: editingSuggestion.regionPathIds,
      regionPathNames: editingSuggestion.regionPathNames,
    };

    return { original, modified };
  };

  const handleApproveWithChanges = async () => {
    if (!editingSuggestion || isSubmitting) return;
    if (!requiredFieldsFilled) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await buildModifiedSuggestion(true);
      if (!result) return;
      handleApproveSuggestion(result.original, result.modified);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEdits = async () => {
    if (!editingSuggestion || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await buildModifiedSuggestion(false);
      if (!result) return;
      await onSaveSuggestion(result.original.id, result.modified);
      toast.success(t("websiteManagement.suggestionSaved"));
      setEditingSuggestion(null);
    } catch {
      toast.error(t("common.genericError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="card-title-text" style={primaryTextStyle}>
            {t("websiteManagement.suggestionsTitle")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("websiteManagement.suggestionsDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingSuggestions.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle
                className="w-12 h-12 mx-auto mb-3"
                style={mutedIconStyle}
              />
              <p className="text-secondary">
                {t("websiteManagement.noSuggestions")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {pagedSuggestions.map((suggestion) => {
                const categoryLabel = buildCategoryBreadcrumbLabel(
                  suggestion.categoryPathNames,
                  suggestion.category1
                );
                const suggestionHasZip = /^\d{5}$/.test(
                  (suggestion.zipCode ?? "").trim()
                );
                const hasRequiredText =
                  Boolean(suggestion.title.trim()) &&
                  Boolean(suggestion.url.trim()) &&
                  Boolean(suggestion.description.trim());
                const locationLabel =
                  suggestion.regionPathNames &&
                  suggestion.regionPathNames.length > 0
                    ? suggestion.regionPathNames.join(" > ")
                    : suggestion.region && suggestion.region.trim().length > 0
                    ? suggestion.region
                    : suggestion.zipCode && suggestion.zipCode.trim().length > 0
                    ? suggestion.zipCode
                    : "—";
                const hasLocation = locationLabel !== "—";
                const categoryPrefix = t("websiteSuggest.categoryPathPrefix");
                const regionPrefix = t("websiteSuggest.regionPathPrefix");
                const canApproveDirectly =
                  hasRequiredText &&
                  Boolean(suggestion.category1Id) &&
                  (Boolean(suggestion.regionId) || suggestionHasZip);

                return (
                  <Card key={suggestion.id} style={suggestionCardStyle}>
                    <CardContent className="pt-6">
                      <div className="mb-4">
                        <div>
                          <h3 className="text-primary-heading mb-1">
                            {suggestion.title}
                          </h3>
                          <p className="text-sm text-secondary flex items-center gap-2 mb-2 break-all">
                            <Globe className="w-3 h-3 flex-shrink-0" style={iconStyle} />
                            <span className="break-all">{suggestion.url}</span>
                          </p>
                          <p className="text-sm mb-3" style={primaryTextStyle}>
                            {suggestion.description}
                          </p>
                          {(categoryLabel || hasLocation) && (
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {categoryLabel && (
                                <Badge
                                  variant="secondary"
                                  className="whitespace-normal break-words text-left max-w-full shrink"
                                >
                                  {categoryPrefix} {categoryLabel}
                                </Badge>
                              )}
                              {hasLocation && (
                                <Badge
                                  variant="secondary"
                                  className="whitespace-normal break-words text-left max-w-full shrink"
                                >
                                  {regionPrefix} {locationLabel}
                                </Badge>
                              )}
                            </div>
                          )}
                          <p className="text-xs" style={secondaryTextStyle}>
                            {t("websiteManagement.suggestedBy")
                              .replace("{name}", suggestion.suggestedBy)
                              .replace(
                                "{date}",
                                new Date(suggestion.suggestedAt).toLocaleDateString("de-DE")
                              )}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {canApproveDirectly && (
                          <Button
                            onClick={() => handleApproveSuggestion(suggestion)}
                            className="gap-2 w-full sm:w-auto"
                          >
                            <CheckCircle className="w-4 h-4" />
                            {t("websiteManagement.approveButton")}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          className="gap-2 w-full sm:w-auto"
                          onClick={() => setEditingSuggestion(toEditFormData(suggestion))}
                        >
                          <Edit className="w-4 h-4" />
                          {t("websiteManagement.editAndApproveButton")}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            onRejectSuggestion(suggestion.id);
                            toast.error(t("websiteManagement.suggestionRejected"));
                          }}
                        >
                          {t("websiteManagement.reject")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
        {pendingSuggestions.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 pb-6">
            <p className="text-xs" style={secondaryTextStyle}>
              {t("common.page")} {currentPage} / {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage <= 1}
              >
                {t("common.prev")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
              >
                {t("common.next")}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {editingSuggestion && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-50"
          style={overlayStyle}
        >
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto stat-card">
            <CardHeader>
              <CardTitle className="card-title-text" style={primaryTextStyle}>
                {t("websiteManagement.editSuggestionTitle")}
              </CardTitle>
              <CardDescription
                className="card-description-text"
                style={secondaryTextStyle}
              >
                {t("websiteManagement.editSuggestionDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label style={primaryTextStyle}>
                  {renderRequiredLabel(t("websiteManagement.titleLabel"))}
                </Label>
                <Input
                  id="suggestion-title"
                  value={editingSuggestion.title}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditingSuggestion({
                      ...editingSuggestion,
                      title: value,
                    });

                    const tooLong = value.length > MAX_TITLE_LEN;
                    if (tooLong && !wasTitleTooLong) {
                      toast.error(
                        t("websiteSuggest.errorTitleTooLong").replace(
                          "{max}",
                          String(MAX_TITLE_LEN)
                        )
                      );
                    }
                    setWasTitleTooLong(tooLong);

                    if (showValidation) {
                      setFieldErrors((prev) => ({ ...prev, title: !value.trim() }));
                    }
                  }}
                  aria-invalid={titleInvalid ? true : undefined}
                />

                {showValidation && fieldErrors.title && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorTitleRequired")}
                  </p>
                )}

                {titleTooLong && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorTitleTooLong").replace(
                      "{max}",
                      String(MAX_TITLE_LEN)
                    )}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label style={primaryTextStyle}>
                  {renderRequiredLabel(t("websiteManagement.urlLabel"))}
                </Label>
                <Input
                  id="suggestion-url"
                  value={editingSuggestion.url}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditingSuggestion({
                      ...editingSuggestion,
                      url: value,
                    });
                    setUrlErrorKey(null);

                    const tooLong = value.length > MAX_URL_LEN;
                    if (tooLong && !wasUrlTooLong) {
                      toast.error(
                        t("websiteSuggest.errorUrlTooLong").replace(
                          "{max}",
                          String(MAX_URL_LEN)
                        )
                      );
                    }
                    setWasUrlTooLong(tooLong);

                    if (showValidation) {
                      setFieldErrors((prev) => ({ ...prev, url: !value.trim() }));
                    }
                  }}
                  aria-invalid={urlInvalid ? true : undefined}
                />

                {urlInvalid && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {urlTooLong
                      ? t("websiteSuggest.errorUrlTooLong").replace(
                          "{max}",
                          String(MAX_URL_LEN)
                        )
                      : urlErrorKey
                      ? t(urlErrorKey)
                      : t("websiteSuggest.errorUrlRequired")}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label style={primaryTextStyle}>
                  {renderRequiredLabel(t("websiteManagement.descriptionLabel"))}
                </Label>
                <Textarea
                  id="suggestion-description"
                  rows={4}
                  value={editingSuggestion.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditingSuggestion({
                      ...editingSuggestion,
                      description: value,
                    });

                    if (showValidation) {
                      setFieldErrors((prev) => ({ ...prev, description: !value.trim() }));
                    }

                    const tooLong = value.length > MAX_DESCRIPTION_LEN;
                    if (tooLong && !wasDescriptionTooLong) {
                      toast.error(
                        t("websiteSuggest.errorDescriptionTooLong").replace(
                          "{max}",
                          String(MAX_DESCRIPTION_LEN)
                        )
                      );
                    }
                    setWasDescriptionTooLong(tooLong);
                  }}
                  aria-invalid={descriptionInvalid ? true : undefined}
                />

                {showValidation && fieldErrors.description && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorDescriptionRequired")}
                  </p>
                )}

                {descriptionTooLong && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorDescriptionTooLong").replace(
                      "{max}",
                      String(MAX_DESCRIPTION_LEN)
                    )}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="suggestion-address" style={primaryTextStyle}>
                  {t("websiteSuggest.addressLabel")}
                </Label>
                <AddressAutocomplete
                  inputId="suggestion-address"
                  value={editingSuggestion.address ?? ""}
                  onChange={(next) => {
                    setEditingSuggestion({
                      ...editingSuggestion,
                      address: next,
                    });
                    setAutoConfidence(null);
                    if (addressAutocompleteEnabled) {
                      setAutoStatus("idle");
                    }
                    setAutoRegionType(null);

                    const tooLong = next.length > MAX_ADDRESS_LEN;
                    if (tooLong && !wasAddressTooLong) {
                      toast.error(
                        t("websiteSuggest.errorAddressTooLong").replace(
                          "{max}",
                          String(MAX_ADDRESS_LEN)
                        )
                      );
                    }
                    setWasAddressTooLong(tooLong);
                  }}
                  onSelect={handleAutoSelect}
                  onStatusChange={setAutoStatus}
                  t={t}
                  suggestionsEnabled={addressAutocompleteEnabled}
                />

                {addressTooLong && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorAddressTooLong").replace(
                      "{max}",
                      String(MAX_ADDRESS_LEN)
                    )}
                  </p>
                )}

                {!addressAutocompleteEnabled && (
                  <p className="text-xs text-secondary">
                    {t("websiteSuggest.addressAutoDisabled")}
                  </p>
                )}

                {addressAutocompleteEnabled &&
                  (autoStatus === "unavailable" ||
                    autoStatus === "invalid_response") && (
                    <p className="text-xs text-secondary">
                      {t("websiteSuggest.addressAutoUnavailable")}
                    </p>
                  )}

                {addressAutocompleteEnabled && confidenceLabel && (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" aria-live="polite">
                      {confidenceLabel}
                    </Badge>
                  </div>
                )}

                {addressAutocompleteEnabled && (
                  <p className="text-xs text-secondary">
                    {t("websiteSuggest.addressAutoHint")}
                  </p>
                )}
              </div>

              <div id="suggestion-region">
                <RegionSelector
                  formData={editingSuggestion}
                  setFormData={setEditingFormData}
                  openRegion={openRegion}
                  setOpenRegion={setOpenRegion}
                  onZipCodeChange={(zip) => {
                    const cleaned = zip.replace(/\D/g, "").slice(0, 5);
                    setEditingSuggestion((prev) =>
                      prev ? { ...prev, zipCode: cleaned } : prev
                    );
                    setAutoRegionApplied(false);

                    const tooLong = cleaned.length > MAX_ZIP_LEN;
                    if (tooLong && !wasZipTooLong) {
                      toast.error(
                        t("websiteSuggest.errorZipTooLong").replace(
                          "{max}",
                          String(MAX_ZIP_LEN)
                        )
                      );
                    }
                    setWasZipTooLong(tooLong);
                  }}
                  t={t}
                  labels={{
                    zipLabel: t("websiteManagement.zipCodeLabel"),
                    zipPlaceholder: t("websiteManagement.zipCodePlaceholder"),
                    zipHint: t("websiteManagement.zipCodeHint"),
                    regionLabel: t("websiteManagement.regionLabel"),
                    regionPlaceholder: t("websiteManagement.regionPlaceholder"),
                    regionHint: t("websiteManagement.regionHint"),
                  }}
                />

                {zipInvalid && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteSuggest.errorZipTooLong").replace(
                      "{max}",
                      String(MAX_ZIP_LEN)
                    )}
                  </p>
                )}

                {regionMissing && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteManagement.regionRequiredError")}
                  </p>
                )}
              </div>

              <div
                id="suggestion-category"
                className="w-full sm:w-auto sm:flex-none min-w-0 sm:max-w-[280px]"
              >
                <CategoryCombobox
                  formData={editingSuggestion}
                  setFormData={setEditingFormData}
                  openCategory1={openCategory1}
                  setOpenCategory1={setOpenCategory1}
                  t={t}
                  language={language}
                  disabled={isSubmitting}
                  labelText={t("websiteSuggest.category")}
                  navigatorHintText={t("websiteSuggest.categoryNavigatorHint")}
                  emptyStateAction={
                    aiFeatureEnabled
                      ? {
                          label: aiLoading
                            ? t("websiteSuggest.aiSuggesting")
                            : t("websiteSuggest.aiSuggestButton"),
                          onClick: () => {
                            void handleAiSuggest();
                          },
                          disabled:
                            aiLoading || isSubmitting || aiMissingRequiredFields,
                          disabledTooltip: aiMissingRequiredFields
                            ? t("websiteSuggest.aiNeedTitleAndDescription")
                            : undefined,
                          icon: <Sparkles className="w-4 h-4" />,
                        }
                      : undefined
                  }
                />

                {categoryMissing && (
                  <p className="text-xs font-semibold" style={errorTextStyle}>
                    {t("websiteManagement.categoryRequiredError")}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                {showApproveTooltip ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="inline-flex"
                        tabIndex={0}
                        onClick={() => {
                          if (!editingSuggestion) return;
                          setShowValidation(true);
                          setShowCategorizationValidation(true);
                          setFieldErrors({
                            title: !editingSuggestion.title.trim(),
                            url: !editingSuggestion.url.trim(),
                            description: !editingSuggestion.description.trim(),
                          });
                        }}
                      >
                        <Button
                          onClick={handleApproveWithChanges}
                          disabled={approveWithChangesDisabled}
                        >
                          {t("websiteManagement.approveWithChanges")}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t("websiteManagement.fillAllFields")}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Button
                    onClick={handleApproveWithChanges}
                    disabled={approveWithChangesDisabled}
                  >
                    {t("websiteManagement.approveWithChanges")}
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={handleSaveEdits}
                  disabled={isSubmitting}
                >
                  {t("websiteManagement.saveSuggestionEdits")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditingSuggestion(null)}
                >
                  {t("websiteManagement.cancel")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
